import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './shared/Button';
import authService from '../services/authService';
import './Settings.css';

function Settings() {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const authToken = authService.getToken();
  const user = authService.getUser();
  const username = user?.username || localStorage.getItem('username');
  const email = user?.email;

  // Check if user is authenticated
  const isAuthenticated = !!authToken;

  const handleDeleteAccount = async () => {
    if (!authToken) {
      alert('You must be logged in to delete your account');
      return;
    }

    setIsDeleting(true);

    try {
      const API_URL =
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_BACKEND_URL ||
        (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3001');

      const response = await fetch(`${API_URL}/api/user/delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Clear all local storage
        localStorage.clear();

        alert(
          `Account deleted successfully!\n\nCleaned from ${data.roomsCleaned} game rooms.\n\nYou will now be redirected to the home page.`
        );

        // Redirect to home
        navigate('/');
      } else {
        throw new Error(data.error || data.message || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(`Failed to delete account: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-card">
        <h1>Account Settings</h1>

        {!isAuthenticated ? (
          <div className="not-authenticated">
            <p>You must be logged in to access settings.</p>
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </div>
        ) : (
          <>
            <div className="account-info">
              <h2>Account Information</h2>
              <p>
                <strong>Email:</strong> {email || 'Not available'}
              </p>
              <p>
                <strong>Username:</strong> {username || 'Not set'}
              </p>
              {user?.createdAt && (
                <p>
                  <strong>Account Created:</strong>{' '}
                  {new Date(user.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>

            <div className="danger-zone">
              <h2>Danger Zone</h2>
              <p>
                Deleting your account will permanently remove all your game data, including scores
                and saved games across all rooms. This action cannot be undone.
              </p>
              <p className="deletion-info">
                <strong>Note:</strong> If you log back in with the same OAuth provider (Google or
                Discord), a <strong>new account</strong> will be created. You can verify deletion
                worked by checking the "Account Created" date above after logging back in.
              </p>

              {!showConfirmation ? (
                <Button
                  variant="danger"
                  onClick={() => setShowConfirmation(true)}
                  disabled={isDeleting}
                >
                  Delete My Account
                </Button>
              ) : (
                <div className="confirmation-box">
                  <p className="confirmation-warning">
                    ⚠️ Are you absolutely sure? This will permanently delete your account and all
                    associated data.
                  </p>
                  <div className="confirmation-buttons">
                    <Button
                      variant="danger"
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      loading={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setShowConfirmation(false)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Button variant="secondary" onClick={() => navigate('/play')} className="back-button">
              ← Back to Home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default Settings;
